import { Alert, Box, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import InfoIcon from "@mui/icons-material/Info";

import {
  DocumentPreview,
  DocumentPreviewProps,
} from "components/Document";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { SignatureType } from "generated-graphql/graphql";
import { useMemo } from "react";

type SubmissionDocumentPreviewProps = {
  previewDocumentUrl: string;
  reviewState: SubmissionReviewState;
  signingCompleted: boolean;
  signatureType?: SignatureType | null;
  isSigner: boolean;
  onSigningDialogOpen: () => void;
};

export function SubmissionDocumentPreview({
  previewDocumentUrl,
  reviewState,
  signingCompleted,
  signatureType,
  isSigner,
  onSigningDialogOpen,
}: SubmissionDocumentPreviewProps) {
  const theme = useTheme();

  const isWetInkSigner = isSigner && signatureType === SignatureType.WetInk;

  const signDocumentProps:
    | DocumentPreviewProps["bottomLeftButtonProps"]
    | undefined = useMemo(() => {
    // Don't show sign button for wet ink signatures
    if (isWetInkSigner) {
      return undefined;
    }

    // Show sign button for digital signatures only
    if (
      reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
      reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
    ) {
      return {
        // Show different text and icon depending on the signing status
        variant: "outlined",
        text: signingCompleted ? "Signature Completed" : "Sign Document",
        startIcon: signingCompleted ? <CheckIcon /> : <BorderColorIcon />,

        // If the signer cannot sign yet then show the button as disabled
        disabled: reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN,
        sx: {
          paddingY: theme.spacing(3),
          paddingX: theme.spacing(4),
          background: signingCompleted ? "#E6F1F7" : "#EAF0EA",
        },
        color: signingCompleted ? "info" : "success",
        onClick: onSigningDialogOpen,
      };
    }
    return undefined;
  }, [reviewState, isWetInkSigner, onSigningDialogOpen, theme, signingCompleted]);

  return (
    <Box sx={{ height: "100%", minHeight: 600, maxHeight: 700 }}>
      {isWetInkSigner && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          This document requires a wet ink signature. Your Encamp contact will
          reach out to coordinate physical document signing.
        </Alert>
      )}
      <DocumentPreview
        fileUrl={previewDocumentUrl}
        bottomLeftButtonProps={signDocumentProps}
      />
    </Box>
  );
}
